/**
 * AnnouncementPopup — "What's New" popup in bottom-right corner.
 * Shows for admin/owner users when there's an undismissed announcement.
 * Auto-embeds Loom videos. Dismissible.
 */

import React, { useState, useEffect } from 'react';
import { X, PartyPopper, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { sanitizeHtml } from '@/utils/sanitize';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function extractLoomId(url) {
  if (!url) return null;
  const match = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
  return match ? match[1] : null;
}

export default function AnnouncementPopup({ userId, userRole }) {
  const [announcement, setAnnouncement] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const isAdminOrOwner = userRole === 'admin' || userRole === 'owner';

  useEffect(() => {
    if (!userId || !isAdminOrOwner) return;

    const fetchLatest = async () => {
      try {
        const res = await fetch(`${API_URL}/api/announcements/latest?user_id=${encodeURIComponent(userId)}`);
        if (res.ok) {
          const data = await res.json();
          if (data.announcement) {
            setAnnouncement(data.announcement);
          }
        }
      } catch {}
      setLoaded(true);
    };

    fetchLatest();
  }, [userId, isAdminOrOwner]);

  const handleDismiss = async () => {
    setDismissed(true);
    if (announcement?.id) {
      try {
        await fetch(`${API_URL}/api/announcements/${announcement.id}/dismiss`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId }),
        });
      } catch {}
    }
  };

  if (!loaded || !announcement || dismissed || !isAdminOrOwner) return null;

  const loomId = extractLoomId(announcement.loom_url);

  return (
    <div
      className="fixed bottom-4 right-4 z-[200] w-80 sm:w-96 animate-in slide-in-from-bottom-4 fade-in duration-300"
      data-testid="announcement-popup"
    >
      <div data-ui="float" className="bg-card rounded-2xl shadow-2xl border border-slate-200 dark:border-white/10 overflow-hidden">
        {/* Header */}
        <div data-ui="wn-head" className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-indigo-50 dark:from-indigo-950/40 to-violet-50 border-b border-slate-100 dark:border-white/10">
          <div className="flex items-center gap-2">
            <PartyPopper className="w-5 h-5 text-indigo-500" />
            <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">What&apos;s New</span>
          </div>
          <button
            onClick={handleDismiss}
            className="w-6 h-6 rounded-full flex items-center justify-center text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/10 transition-colors"
            data-testid="announcement-dismiss-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div data-ui="wn-body" className="px-4 py-3">
          <div
            className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed announcement-content"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(announcement.message) }}
          />

          {/* Loom embed */}
          {loomId && (
            <div className="mt-3">
              <div className="aspect-video rounded-lg overflow-hidden bg-slate-100 dark:bg-[#3a3d41]">
                <iframe
                  src={`https://www.loom.com/embed/${loomId}?hide_owner=true&hide_share=true&hide_title=true`}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  title="Loom video"
                />
              </div>
              <div className="mt-1 text-right">
                <a
                  href={announcement.loom_url || `https://www.loom.com/share/${loomId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[10px] text-slate-400 dark:text-slate-500 hover:text-indigo-500 dark:hover:text-indigo-400"
                  data-testid="announcement-popup-fallback"
                >
                  <ExternalLink className="w-2.5 h-2.5" />
                  Trouble playing? Open in Loom
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div data-ui="wn-foot" className="px-4 pb-3">
          <Button
            onClick={handleDismiss}
            size="sm"
            className="w-full rounded-xl bg-indigo-600 hover:bg-indigo-700 text-xs h-8"
            data-testid="announcement-got-it-btn"
          >
            Got it!
          </Button>
        </div>
      </div>
    </div>
  );
}
